import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { renderSbIcon } from '../IconPickerField';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const IconBoxes: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || 'grid';
  const isList = variant === 'list';

  const colCount = c.columns === 2 || c.columns === '2' ? 2
    : c.columns === 4 || c.columns === '4' ? 4
    : 3;

  // 'boxes' is the canonical control key (matching schema + Python renderer);
  // fall back to 'items' for any legacy data that used the old key.
  const items = (c.boxes || c.items || [
    { icon: '🚀', title: 'Fast Delivery', description: 'We ship features and results at speed, without sacrificing quality.', link_text: '', link_url: '' },
    { icon: '🔒', title: 'Enterprise Security', description: 'SOC 2 compliant infrastructure keeping your data safe at all times.', link_text: '', link_url: '' },
    { icon: '📊', title: 'Data Insights', description: 'Real-time analytics and reporting so you always know what is working.', link_text: '', link_url: '' },
    { icon: '🤝', title: 'Expert Support', description: 'Dedicated account managers available whenever you need them.', link_text: '', link_url: '' },
    { icon: '⚙️', title: 'Integrations', description: 'Connects with 200+ tools in your existing stack seamlessly.', link_text: '', link_url: '' },
    { icon: '🌍', title: 'Global Scale', description: 'Infrastructure in 30+ regions handles any volume, anywhere.', link_text: '', link_url: '' },
  ]) as Array<{ icon: string; title: string; description: string; link_text?: string; link_url?: string; url?: string }>;

  return (
    <section className="sb-render sb-render-icon-boxes" style={{ padding: '80px 24px', background: 'var(--brand-bg, #fff)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 12, letterSpacing: '-0.025em' }}
          >
            {sfHeadline(c, 'What We Offer')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subtitle"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 560, margin: '0 auto' }}
          >
            {c.subtitle || sfSub(c, 'Everything you need to succeed, all in one place.')}
          </InlineEditText>
        </div>

        {isList ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 800, margin: '0 auto' }}>
            {items.map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 24,
                  alignItems: 'flex-start',
                  padding: '28px 32px',
                  background: 'var(--brand-bg, #fff)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 'var(--brand-card-radius, 12px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.25s, border-color 0.25s',
                }}
              >
                <div style={{
                  width: 60, height: 60, borderRadius: 14,
                  background: 'linear-gradient(135deg, rgba(37,99,235,0.1), rgba(124,58,237,0.08))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.75rem', flexShrink: 0,
                }}>
                  {renderSbIcon(item.icon, 28)}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 6 }}>{item.title}</h3>
                  <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.65, fontSize: '0.95rem', marginBottom: item.url ? 12 : 0 }}>{item.description}</p>
                  {(item.link_url || item.url) && (
                    <a href={item.link_url || item.url} style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none' }}>
                      {item.link_text || 'Learn more'} →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${colCount}, 1fr)`,
            gap: 24,
          }}>
            {items.map((item, i) => {
              const linkUrl = item.link_url || item.url;
              return (
              <div
                key={i}
                style={{
                  padding: '36px 28px',
                  background: 'var(--brand-bg, #fff)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 'var(--brand-card-radius, 16px)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                  textAlign: 'center',
                  transition: 'box-shadow 0.25s, transform 0.25s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div style={{
                  width: 72, height: 72, borderRadius: 18,
                  background: 'linear-gradient(135deg, rgba(37,99,235,0.1), rgba(124,58,237,0.08))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '2.25rem', marginBottom: 20,
                }}>
                  {renderSbIcon(item.icon, 32)}
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 10 }}>{item.title}</h3>
                <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.65, fontSize: '0.9rem', marginBottom: linkUrl ? 16 : 0 }}>{item.description}</p>
                {linkUrl && (
                  <a href={linkUrl} style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none', marginTop: 'auto' }}>
                    {item.link_text || 'Learn more'} →
                  </a>
                )}
              </div>
            );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default IconBoxes;
